import {
  getAllProducts,
  getProductById
} from '@/modules/admin/products/products/api/products.service'

import { getAllProductTypes } from '@/modules/admin/products/product-types/api/productTypes.service'
import { getAllBrands } from '@/modules/admin/brands/api/brands.service'
import { getAllCategories } from '@/modules/admin/categories/api/categories.service'
import http from '@/shared/api/http'

import {
  mapResolvedProduct,
  mapResolvedProducts
} from '@/modules/catalog/model/product.mapper'

function preserveAttributeIds(attr) {
  return {
    ...attr,
    values: attr.values ?? [],
    selectedValueId: attr.selectedValueId ?? null
  }
}

export async function getProductVariantsByProductId(productId) {
  const normalizedId = Number(productId)

  if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
    throw new Error(`Некорректный productId: ${productId}`)
  }

  try {
    return (await http.get(`products/${normalizedId}/variants`)).data ?? []
  } catch (error) {
    if (error?.response?.status === 404) {
      return []
    }

    throw error
  }
}

export function summarizeProductVariants(variants) {
  if (!Array.isArray(variants) || variants.length === 0) {
    return null
  }

  const validVariants = variants.filter((variant) => variant && variant.price !== undefined && variant.price !== null)
  if (validVariants.length === 0) {
    return null
  }

  const defaultVariant = validVariants.find((variant) => variant.isDefault) || validVariants[0]
  const minVariant = validVariants.reduce((current, next) => {
    const currentPrice = Number(current.price)
    const nextPrice = Number(next.price)

    if (Number.isNaN(currentPrice)) return next
    if (Number.isNaN(nextPrice)) return current

    return nextPrice < currentPrice ? next : current
  }, validVariants[0])

  const variantPrices = validVariants.map((variant) => Number(variant.price)).filter((price) => !Number.isNaN(price))
  const uniquePrices = Array.from(new Set(variantPrices))

  return {
    variants: validVariants,
    defaultVariant,
    minVariant,
    priceIsFrom: uniquePrices.length > 1 || defaultVariant.id !== minVariant.id,
  }
}

export async function getProductVariantsSummaryByProductId(productId) {
  const variants = await getProductVariantsByProductId(productId)
  return summarizeProductVariants(variants)
}

export async function getResolvedProducts() {
  const [products, productTypes, brands, categories] = await Promise.all([
    getAllProducts(),
    getAllProductTypes(),
    getAllBrands(),
    getAllCategories()
  ])

  const productsWithAttrs = products.map(p => ({
    ...p,
    attributes: (p.attributes ?? []).map(preserveAttributeIds)
  }))

  return mapResolvedProducts(productsWithAttrs, productTypes, brands, categories)
}

export async function getResolvedProductById(productId) {
  const normalizedId = Number(productId)

  if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
    throw new Error(`Некорректный productId: ${productId}`)
  }

  const [product, productTypes, brands, categories] = await Promise.all([
    getProductById(normalizedId),
    getAllProductTypes(),
    getAllBrands(),
    getAllCategories()
  ])

  const productWithAttrs = {
    ...product,
    attributes: (product.attributes ?? []).map(preserveAttributeIds)
  }

  return mapResolvedProduct(productWithAttrs, productTypes, brands, categories)
}
function getDefaultImageUrl(images) {
  if (!Array.isArray(images) || images.length === 0) {
    return null
  }

  const defaultImage =
    images.find(image => (image.isDefault ?? image.IsDefault) === true) ||
    images[0]

  return defaultImage?.url ?? defaultImage?.Url ?? null
}

function mapCatalogCard(card) {
  const productType = card?.productType ?? card?.ProductType ?? {}
  const defaultProduct = card?.defaultProduct ?? card?.DefaultProduct ?? {}

  const salePrice = defaultProduct.salePrice ?? defaultProduct.SalePrice ?? null
  const salePriceKzt =
    defaultProduct.salePriceKzt ?? defaultProduct.SalePriceKzt ?? null

  return {
    id:
      card?.productId ??
      card?.ProductId ??
      defaultProduct.id ??
      defaultProduct.Id ??
      null,

    productTypeId:
      card?.productTypeId ??
      card?.ProductTypeId ??
      productType.id ??
      productType.Id ??
      null,

    name: productType.name ?? productType.Name ?? 'Без названия',
    description: productType.description ?? productType.Description ?? '',

    brandId: productType.brandId ?? productType.BrandId ?? null,
    brandName: productType.brandName ?? productType.BrandName ?? 'Без бренда',

    categoryId: productType.categoryId ?? productType.CategoryId ?? null,
    categoryName:
      productType.categoryName ??
      productType.CategoryName ??
      'Без категории',

    isActive: productType.isActive ?? productType.IsActive ?? false,

    articul: defaultProduct.articul ?? defaultProduct.Articul ?? '',

    basePrice:
      defaultProduct.basePrice ??
      defaultProduct.BasePrice ??
      0,

    salePrice,

    saleIsActive:
      defaultProduct.saleIsActive ??
      defaultProduct.SaleIsActive ??
      false,

    basePriceKzt:
      defaultProduct.basePriceKzt ??
      defaultProduct.BasePriceKzt ??
      null,

    salePriceKzt,

    effectivePrice:
      defaultProduct.effectivePrice ??
      defaultProduct.EffectivePrice ??
      null,

    effectivePriceKzt:
      defaultProduct.effectivePriceKzt ??
      defaultProduct.EffectivePriceKzt ??
      null,

    rateUsed:
      defaultProduct.rateUsed ??
      defaultProduct.RateUsed ??
      null,

    quantity:
      defaultProduct.quantity ??
      defaultProduct.Quantity ??
      0,

    images:
      defaultProduct.images ??
      defaultProduct.Images ??
      [],

    defaultImageUrl: getDefaultImageUrl(
      defaultProduct.images ?? defaultProduct.Images ?? []
    ),

    commonAttributes:
      card?.commonAttributes ??
      card?.CommonAttributes ??
      []
  }
}

export async function getCatalogProducts() {
  const response = await http.get('catalog/products')
  const data = response.data ?? []

  if (!Array.isArray(data)) {
    return []
  }

  return data.map(mapCatalogCard)
}

export async function getCatalogProductFamily(productId) {
  const normalizedId = Number(productId)

  if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
    throw new Error(`Некорректный productId: ${productId}`)
  }

  const response = await http.get(`catalog/products/${normalizedId}`)
  return response.data
}

export async function queryProductTypeVariants(productTypeId, selectedValues) {
  const normalizedId = Number(productTypeId)
  if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
    throw new Error(`Некорректный productTypeId: ${productTypeId}`)
  }
  const response = await http.post(`product-types/${normalizedId}/variants/query`, {
    selectedValues
  })
  return response.data
}
